/**
=========================================================
* Trust Cargo - audit log (سجل الحركات)
=========================================================

* Every add, edit and delete anywhere in the system: who did it, when, to
* what - and, opened, exactly what changed: a new record's values, an
* edited one's before and after, a deleted one as it was.
*
* The server keeps the log by itself (App\Support\AuditTrail) around every
* change sent to it; this page only reads it. Newest first, a page at a
* time, filtered on the server - the log grows with every save.
*
* Admins only, like Backups: it shows everybody's work.
*/

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";

import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Table, TableCell, TableHead, TableBody, TableRow } from "@mui/material";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar from "components/FilterBar";
import { FilterSelect, FilterDates } from "components/FilterBar/controls";
import DialogShell from "layouts/accounting/DialogShell";
import { Section } from "layouts/accounting/Fields";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

const API = "https://trust-cargo.co/anas_backend/api/v2/audit-logs";
const PER_PAGE = 50;

const headers = () => ({
  Accept: "application/json",
  Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
});

/** What a failed call means for the person looking at the screen. */
const problemOf = (e) => {
  const status = e?.response?.status;
  if (status === 401) return "انتهت جلسة الدخول. سجّل خروج وادخل من جديد عشان تشوف سجل الحركات.";
  if (status === 403) return e?.response?.data?.message || "ما عندك صلاحية لسجل الحركات.";
  return e?.response?.data?.message || "Could not load the audit log";
};

export const ACTIONS = {
  create: { label: "إضافة · Added", color: "#2E7D32" },
  update: { label: "تعديل · Edited", color: "#1565C0" },
  delete: { label: "حذف · Deleted", color: "#B23A2E" },
  other: { label: "أخرى · Other", color: "#5F6B7A" },
};

/** "2026-11-05 14:03" in the office's own time. */
export const when = (at) => {
  if (!at) return "-";
  const d = new Date(String(at).replace(" ", "T") + (String(at).endsWith("Z") ? "" : "Z"));
  if (Number.isNaN(d.getTime())) return String(at);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const show = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

function ActionChip({ action }) {
  const a = ACTIONS[action] || ACTIONS.other;
  return (
    <Chip
      size="small"
      label={a.label}
      sx={{
        fontWeight: 700,
        color: a.color,
        backgroundColor: `${a.color}1A`,
        border: `1px solid ${a.color}33`,
      }}
    />
  );
}

ActionChip.propTypes = { action: PropTypes.string.isRequired };

const mono = { fontFamily: "monospace", fontSize: 12, direction: "ltr", unicodeBidi: "plaintext" };

/** A record's fields, one per line. */
function Values({ values }) {
  const entries = Object.entries(values || {});
  if (!entries.length) return <SuiTypography sx={{ fontSize: 13 }}>—</SuiTypography>;
  return (
    <Table size="small">
      <TableBody>
        {entries.map(([k, v]) => (
          <TableRow key={k}>
            <TableCell sx={{ ...mono, width: "35%", opacity: 0.7 }}>{k}</TableCell>
            <TableCell sx={{ fontSize: 13 }}>{show(v)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

Values.defaultProps = { values: {} };
Values.propTypes = { values: PropTypes.objectOf(PropTypes.any) };

/** One statement the change wrote, in words. */
export function Change({ write }) {
  if (write.op === "more")
    return (
      <SuiTypography sx={{ fontSize: 13, opacity: 0.7 }}>
        {`+ ${write.count} تغييرات أخرى · more changes not listed`}
      </SuiTypography>
    );

  const title = {
    create: "سطر جديد · New row",
    update: "تعديل · Changed",
    delete: "انحذف · Removed",
  }[write.op];

  return (
    <SuiBox mb={2}>
      <SuiBox display="flex" alignItems="center" gap={1} mb={0.75}>
        <ActionChip action={write.op} />
        <SuiTypography sx={{ fontSize: 13, fontWeight: 700 }}>{title}</SuiTypography>
        <SuiBox component="span" sx={{ ...mono, opacity: 0.6 }}>
          {`${write.table}${write.id ? ` #${write.id}` : ""}${
            write.count ? ` ×${write.count}` : ""
          }`}
        </SuiBox>
      </SuiBox>

      {write.op === "create" && <Values values={write.values} />}

      {write.op === "update" && write.set && <Values values={write.set} />}

      {write.op === "update" &&
        (write.rows || []).map((r) => (
          <SuiBox key={r.id} mb={1}>
            <SuiTypography sx={{ fontSize: 12, opacity: 0.7, mb: 0.5 }}>
              {`#${r.id}${r.label ? ` · ${r.label}` : ""}`}
            </SuiTypography>
            <Table size="small" aria-label={`changes ${r.id}`}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: 12 }}>الحقل · Field</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>قبل · Before</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>بعد · After</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(r.changes || {}).map(([col, [was, now]]) => (
                  <TableRow key={col}>
                    <TableCell sx={{ ...mono, opacity: 0.7 }}>{col}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#B23A2E" }}>{show(was)}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#2E7D32", fontWeight: 600 }}>
                      {show(now)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </SuiBox>
        ))}

      {write.op === "delete" &&
        (write.rows || []).map((r, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <SuiBox key={r.id ?? i} mb={1}>
            <Values values={r} />
          </SuiBox>
        ))}
    </SuiBox>
  );
}

Change.propTypes = {
  write: PropTypes.shape({
    op: PropTypes.string,
    table: PropTypes.string,
    id: PropTypes.string,
    count: PropTypes.number,
    values: PropTypes.objectOf(PropTypes.any),
    set: PropTypes.objectOf(PropTypes.any),
    rows: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
};

function EntryDialog({ entryId, onClose }) {
  const [entry, setEntry] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!entryId) return;
    setEntry(null);
    setError("");
    axios
      .get(`${API}/${entryId}`, { headers: headers() })
      .then((r) => setEntry(r.data))
      .catch((e) => setError(problemOf(e)));
  }, [entryId]);

  const changes = Array.isArray(entry?.changes) ? entry.changes : [];

  return (
    <DialogShell
      open={Boolean(entryId)}
      title={entry ? `${entry.subject_label} · ${entry.record || entry.record_id || ""}` : "…"}
      onClose={onClose}
      error={error}
      maxWidth="md"
    >
      {!entry && !error && <CargoLoader />}
      {entry && (
        <>
          <Section title="الحركة · What happened">
            <SuiBox display="flex" flexWrap="wrap" gap={1.5} alignItems="center" mb={2}>
              <ActionChip action={entry.action} />
              {entry.note && <Chip size="small" label={entry.note} />}
              {!entry.ok && (
                <Chip
                  size="small"
                  color="error"
                  label={`رفضها السيرفر · refused (${entry.status})`}
                />
              )}
            </SuiBox>
            <Values
              values={{
                "مين · Who": entry.user_name
                  ? `${entry.user_name}${entry.user_role ? ` (${entry.user_role})` : ""}`
                  : "غير معروف · not signed in",
                "إمتى · When": when(entry.created_at),
                "السجل · Record": [entry.record, entry.record_id && `#${entry.record_id}`]
                  .filter(Boolean)
                  .join(" "),
                "الطلب · Request": `${entry.method} ${entry.path}`,
                IP: entry.ip,
              }}
            />
          </Section>

          <Section title="شو تغيّر · What changed">
            {changes.length ? (
              changes.map((w, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Change key={i} write={w} />
              ))
            ) : (
              <SuiTypography sx={{ fontSize: 13, opacity: 0.7, mb: 2 }}>
                ما في تغيير مسجّل على البيانات · No rows were changed
              </SuiTypography>
            )}
          </Section>

          {entry.payload && (
            <Section title="شو انبعت · What was sent">
              <Values values={entry.payload} />
            </Section>
          )}
        </>
      )}
    </DialogShell>
  );
}

EntryDialog.defaultProps = { entryId: null };
EntryDialog.propTypes = {
  entryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
};

function AuditLog() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [subjects, setSubjects] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);

  const [query, setQuery] = useState("");
  const [typed, setTyped] = useState("");
  const [userId, setUserId] = useState("");
  const [action, setAction] = useState("");
  const [subject, setSubject] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [failed, setFailed] = useState(false);

  // the search goes to the server once typing stops
  useEffect(() => {
    const t = setTimeout(() => setQuery(typed.trim()), 400);
    return () => clearTimeout(t);
  }, [typed]);

  // a new filter starts again from the newest
  useEffect(() => setPage(1), [query, userId, action, subject, from, to, failed]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError("");
    const params = { page, per_page: PER_PAGE };
    if (query) params.q = query;
    if (userId) params.user_id = userId;
    if (action) params.action = action;
    if (subject) params.subject = subject;
    if (from) params.from = from;
    if (to) params.to = to;
    if (failed) params.failed = 1;

    axios
      .get(API, { headers: headers(), params })
      .then((r) => {
        if (!live) return;
        setRows(r.data?.rows || []);
        setTotal(r.data?.total || 0);
        setUsers(r.data?.users || []);
        setSubjects(r.data?.subjects || {});
      })
      .catch((e) => live && setError(problemOf(e)))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [page, query, userId, action, subject, from, to, failed]);

  const pages = Math.max(1, Math.ceil(total / PER_PAGE));
  const cell = { fontSize: 13 };

  return (
    <DashboardLayout>
      <SuiBox py={1}>
        <SuiBox mb={3}>
          <Card>
            <FilterBar
              title="سجل الحركات · Audit Log"
              value={typed}
              onChange={setTyped}
              count={rows.length}
              total={total}
              placeholder="ابحث بالاسم، الكود، المستخدم… · Search"
            >
              <FilterSelect
                label="المستخدم · User"
                icon="person"
                value={userId}
                onChange={setUserId}
                anyLabel="الكل · All"
                options={[
                  ...users.map((u) => ({ value: String(u.user_id), label: u.user_name })),
                  { value: "none", label: "بدون تسجيل دخول · Not signed in" },
                ]}
              />
              <FilterSelect
                label="الحركة · Action"
                icon="bolt"
                value={action}
                onChange={setAction}
                anyLabel="الكل · All"
                options={Object.entries(ACTIONS).map(([value, a]) => ({ value, label: a.label }))}
              />
              <FilterSelect
                label="على · On"
                icon="category"
                value={subject}
                onChange={setSubject}
                anyLabel="الكل · All"
                options={Object.entries(subjects).map(([value, label]) => ({ value, label }))}
              />
              <FilterDates
                from={from}
                to={to}
                onFrom={setFrom}
                onTo={setTo}
                label="التاريخ · Date"
              />
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={failed}
                    onChange={(e) => setFailed(e.target.checked)}
                  />
                }
                label={
                  <SuiTypography sx={{ fontSize: 12 }}>
                    المحاولات المرفوضة · Refused attempts
                  </SuiTypography>
                }
              />
            </FilterBar>

            {error && (
              <SuiBox px={3} pb={2}>
                <Alert severity="error">{error}</Alert>
              </SuiBox>
            )}

            {loading ? (
              <CargoLoader />
            ) : (
              <SuiBox sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 960 }} aria-label="audit log">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">الوقت · When</TableCell>
                      <TableCell align="center">المستخدم · Who</TableCell>
                      <TableCell align="center">الحركة · Action</TableCell>
                      <TableCell align="center">على · On</TableCell>
                      <TableCell align="center">السجل · Record</TableCell>
                      <TableCell align="center">التفاصيل · Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.id} hover sx={{ opacity: r.ok ? 1 : 0.6 }}>
                        <TableCell align="center" sx={{ ...cell, whiteSpace: "nowrap" }}>
                          {when(r.created_at)}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {r.user_name || (
                            <SuiBox component="span" sx={{ opacity: 0.6 }}>
                              غير معروف · unknown
                            </SuiBox>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <ActionChip action={r.action} />
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {r.subject_label}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {[r.record, r.record_id && !r.record ? `#${r.record_id}` : null]
                            .filter(Boolean)
                            .join(" ") || "—"}
                          {r.note && (
                            <SuiBox component="div" sx={{ fontSize: 12, opacity: 0.65 }}>
                              {r.note}
                            </SuiBox>
                          )}
                          {!r.ok && (
                            <Chip
                              size="small"
                              color="error"
                              label={`مرفوض · refused ${r.status}`}
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            color="dark"
                            onClick={() => setOpen(r.id)}
                          >
                            {r.change_count ? `عرض · View (${r.change_count})` : "عرض · View"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!rows.length && !error && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <SuiTypography variant="button" color="text">
                            ما في حركات · No changes recorded
                          </SuiTypography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </SuiBox>
            )}

            <SuiBox display="flex" justifyContent="center" alignItems="center" gap={2} py={2}>
              <Button
                size="small"
                variant="outlined"
                color="dark"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => p - 1)}
              >
                ‹ الأحدث · Newer
              </Button>
              <SuiTypography sx={{ fontSize: 13 }}>{`${page} / ${pages}`}</SuiTypography>
              <Button
                size="small"
                variant="outlined"
                color="dark"
                disabled={page >= pages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                الأقدم · Older ›
              </Button>
            </SuiBox>
          </Card>
        </SuiBox>
      </SuiBox>
      <EntryDialog entryId={open} onClose={() => setOpen(null)} />
      <Footer />
    </DashboardLayout>
  );
}

export default AuditLog;
